import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { formConfig } from '../../config/formConfig';

const FormRenderer = ({ form, isPublicMode = false, route }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({});
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const currentSection = form.sections[currentSectionIndex];

  const handleCopyLink = () => {
    // 1) Use the branded domain from config, fallback to window.location.origin
    const base = formConfig.site.publicBaseUrl || formConfig.site.baseUrl || window.location.origin;
    
    // 2) Build share URL using the branded base
    const url = base + route.sharePath + "?k=" + route.shareKey;
    
    // 3) Copy to clipboard
    navigator.clipboard.writeText(url);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleInput = (id, value) => {
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const isFieldVisible = (field) => {
    if (!field.visibility) return true;
    const { dependsOnFieldId, condition, value } = field.visibility;
    const dependentValue = formData[dependsOnFieldId];
    switch (condition) {
      case 'equals': return dependentValue === value;
      case 'notEquals': return dependentValue !== value;
      case 'in': return Array.isArray(value) ? value.includes(dependentValue) : false;
      case 'notIn': return Array.isArray(value) ? !value.includes(dependentValue) : true;
      default: return true;
    }
  };

  const validateField = (field) => {
    if (!isFieldVisible(field)) return true;
    const val = formData[field.id];
    if (field.required) {
      if (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0) || val === false) return false;
    }
    if (val && field.validation) {
      const { minLength, maxLength, min, max, pattern } = field.validation;
      if (minLength && val.length < minLength) return false;
      if (maxLength && val.length > maxLength) return false;
      if (min && Number(val) < min) return false;
      if (max && Number(val) > max) return false;
      if (pattern && !new RegExp(pattern).test(val)) return false;
    }
    return true;
  };

  const isSectionValid = () => {
    return currentSection.fieldIds.every(fieldId => {
      const field = form.fields.find(f => f.id === fieldId);
      return validateField(field);
    });
  };

  const sendEmails = async (submissionId, submittedAt) => {
    const emailRoute = formConfig.email.routes.find(r => r.formId === form.id);
    if (!emailRoute) return;

    const provider = formConfig.email.provider;
    const apiKey = import.meta.env[provider.apiKeyEnvVar];
    if (!apiKey) {
      console.warn('Email API Key missing. Skipping email workflow.');
      return;
    }

    const replacePlaceholders = (text) => {
      let result = text
        .replace(/{{form\.id}}/g, form.id)
        .replace(/{{form\.name}}/g, form.name)
        .replace(/{{submissionId}}/g, submissionId)
        .replace(/{{submittedAt}}/g, submittedAt)
        .replace(/{{app\.name}}/g, formConfig.app.name);

      const fieldMatches = result.match(/{{submission\.([a-zA-Z0-9_-]+)}}/g);
      if (fieldMatches) {
        fieldMatches.forEach(match => {
          const fieldId = match.replace('{{submission.', '').replace('}}', '');
          result = result.replace(match, formData[fieldId] || '');
        });
      }
      return result;
    };

    let replyTo = provider.replyToStrategy.fallbackAddress;
    if (provider.replyToStrategy.useClientEmailIfAvailable) {
      const clientEmailField = form.fields.find(f => f.type === 'email');
      if (clientEmailField && formData[clientEmailField.id]) {
        replyTo = formData[clientEmailField.id];
      }
    }

    const flows = ['clientConfirmation', 'internalNotification'];
    for (const flow of flows) {
      const template = emailRoute.templates[flow];
      if (!template || !template.enabled) continue;

      let toRecipients = [];
      if (flow === 'internalNotification') {
        toRecipients = ['support@bpasolutions.au'];
      } else {
        const toConfig = emailRoute.recipients.to[0];
        if (toConfig.type === 'field') toRecipients = [formData[toConfig.value]];
        else toRecipients = [toConfig.value];
      }

      const payload = {
        to: toRecipients,
        cc: emailRoute.recipients.cc || [],
        bcc: emailRoute.recipients.bcc || [],
        subject: replacePlaceholders(template.subject),
        text: replacePlaceholders(template.bodyText),
        html: replacePlaceholders(template.bodyHtml),
        replyTo: replyTo,
        tags: ["bpa-forms-hub", form.id, flow],
        metadata: { submissionId, formId: form.id }
      };

      try {
        await fetch(provider.apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
          body: JSON.stringify(payload)
        });
      } catch (err) {
        console.error(`Failed to send ${flow} email:`, err);
      }
    }
  };

  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    if (!isSectionValid()) return;
    setIsSubmitting(true);
    const submissionId = window.crypto?.randomUUID ? crypto.randomUUID() : `sub-${Date.now()}`;
    const submittedAt = new Date().toISOString();
    await new Promise(r => setTimeout(r, 1500));
    await sendEmails(submissionId, submittedAt);
    setSubmissionStatus('success');
    setIsSubmitting(false);
    if (form.submission.redirectUrl) {
      setTimeout(() => navigate(form.submission.redirectUrl), 3000);
    }
  };

  if (submissionStatus === 'success') {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-2xl mx-auto text-center py-20 bg-[var(--surface)] rounded-3xl border border-[var(--border)] shadow-2xl">
        <div className="w-20 h-20 bg-[var(--success)]/10 text-[var(--success)] rounded-full flex items-center justify-center mx-auto mb-6">
          <SafeIcon name="CheckCircle" className="text-4xl" />
        </div>
        <h2 className="text-3xl font-black text-[var(--text)] mb-4">Submission Successful!</h2>
        <p className="text-[var(--textMuted)] px-10 leading-relaxed text-lg">{form.submission.successMessage}</p>
        {!isPublicMode && (
          <button onClick={() => navigate('/')} className="mt-8 px-8 py-3 bg-[var(--primary)] text-white font-bold rounded-xl hover:opacity-90 transition-opacity">Return to Hub</button>
        )}
      </motion.div>
    );
  }

  const renderField = (fieldId) => {
    const field = form.fields.find(f => f.id === fieldId);
    if (!field || !isFieldVisible(field)) return null;
    const commonClass = "w-full bg-[var(--background)] border border-[var(--border)] rounded-[var(--input-borderRadius)] px-[var(--input-paddingX)] py-[var(--input-paddingY)] text-[var(--text)] focus:ring-2 focus:ring-[var(--primary)] outline-none transition-all placeholder:text-[var(--textMuted)]/40";
    return (
      <div key={fieldId} className="flex flex-col gap-2 group">
        <label className="text-sm font-bold text-[var(--text)] flex items-center gap-1.5">
          {field.label}
          {field.required && <span className="text-[var(--danger)] text-lg leading-none">*</span>}
        </label>
        {field.type === 'textarea' && (
          <textarea className={`${commonClass} min-h-[140px] resize-none`} placeholder={field.placeholder} value={formData[fieldId] || ''} onChange={(e) => handleInput(fieldId, e.target.value)} required={field.required} />
        )}
        {field.type === 'select' && (
          <select className={commonClass} value={formData[fieldId] || ''} onChange={(e) => handleInput(fieldId, e.target.value)} required={field.required}>
            <option value="">Select option...</option>
            {field.options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
        )}
        {field.type === 'multiselect' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-[var(--surfaceAlt)]/30 p-4 rounded-2xl border border-[var(--border)]">
            {field.options.map(opt => {
              const current = formData[fieldId] || [];
              const isChecked = current.includes(opt.value);
              return (
                <label key={opt.value} className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${isChecked ? 'bg-[var(--primarySoft)] border-[var(--primary)]' : 'bg-[var(--surface)] border-[var(--border)] hover:border-[var(--primary)]'}`}>
                  <input type="checkbox" className="w-5 h-5 accent-[var(--primary)]" checked={isChecked} onChange={(e) => {
                    const next = e.target.checked ? [...current, opt.value] : current.filter(v => v !== opt.value);
                    handleInput(fieldId, next);
                  }} />
                  <span className="text-sm font-medium">{opt.label}</span>
                </label>
              );
            })}
          </div>
        )}
        {field.type === 'checkbox' && (
          <label className="flex items-start gap-3 p-4 bg-[var(--surfaceAlt)]/30 rounded-2xl border border-[var(--border)] cursor-pointer hover:bg-[var(--surfaceAlt)] transition-colors">
            <input type="checkbox" className="mt-1 w-5 h-5 accent-[var(--primary)]" checked={formData[fieldId] || false} onChange={(e) => handleInput(fieldId, e.target.checked)} />
            <span className="text-sm leading-relaxed text-[var(--text)] font-medium">{field.label}</span>
          </label>
        )}
        {field.type === 'toggle' && (
          <div className="flex items-center justify-between p-4 bg-[var(--surfaceAlt)]/30 rounded-2xl border border-[var(--border)]">
             <span className="text-sm font-bold">{field.label}</span>
             <button type="button" onClick={() => handleInput(fieldId, !formData[fieldId])} className={`w-12 h-6 rounded-full transition-colors relative ${formData[fieldId] ? 'bg-[var(--primary)]' : 'bg-gray-400'}`} >
               <motion.div animate={{ x: formData[fieldId] ? 24 : 4 }} className="w-4 h-4 bg-white rounded-full mt-1" />
             </button>
          </div>
        )}
        {['text', 'email', 'tel', 'url', 'number', 'date'].includes(field.type) && (
          <input type={field.type} className={commonClass} placeholder={field.placeholder} value={formData[fieldId] || ''} onChange={(e) => handleInput(fieldId, e.target.value)} required={field.required} />
        )}
        {field.helperText && <p className="text-[11px] font-medium text-[var(--textMuted)] px-1">{field.helperText}</p>}
      </div>
    );
  };

  return (
    <div className="max-w-3xl mx-auto pb-20">
      <div className="mb-12">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[var(--primarySoft)] text-[var(--primary)] flex items-center justify-center">
              <SafeIcon name={form.icon} className="text-2xl" />
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-widest text-[var(--primary)]">{form.category}</span>
              <h2 className="text-4xl font-black text-[var(--text)] tracking-tight leading-none">{form.name}</h2>
            </div>
          </div>
          {!isPublicMode && (
            <button
              onClick={handleCopyLink}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all text-xs font-bold ${copySuccess ? 'bg-[var(--success)] text-white border-[var(--success)]' : 'bg-[var(--surfaceAlt)] text-[var(--text)] border-[var(--border)] hover:bg-[var(--surface)]'}`}
            >
              <SafeIcon name={copySuccess ? "Check" : "Copy"} />
              {copySuccess ? "Copied!" : "Copy Share Link"}
            </button>
          )}
        </div>
        <p className="text-[var(--textMuted)] text-lg leading-relaxed max-w-xl">{form.description}</p>
      </div>

      {form.layout.showOverallProgress && (
        <div className="mb-10">
          <div className="flex justify-between items-end mb-3">
            <span className="text-[10px] font-black uppercase tracking-widest text-[var(--textMuted)]">Overall Progress</span>
            <span className="text-sm font-black text-[var(--primary)]">
              {Math.round(((currentSectionIndex + 1) / form.sections.length) * 100)}%
            </span>
          </div>
          <div className="h-2 bg-[var(--surfaceAlt)] rounded-full overflow-hidden">
            <motion.div animate={{ width: `${((currentSectionIndex + 1) / form.sections.length) * 100}%` }} className="h-full bg-[var(--primary)]" />
          </div>
        </div>
      )}

      <AnimatePresence mode="wait">
        <motion.div key={currentSection.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="bg-[var(--surface)] border border-[var(--border)] rounded-[var(--card-borderRadius)] p-10 shadow-2xl relative overflow-hidden" >
          <div className="absolute top-0 left-0 w-1 h-full bg-[var(--primary)] opacity-20" />
          <div className="mb-8">
            <h3 className="text-2xl font-black text-[var(--text)] mb-2 tracking-tight">{currentSection.title}</h3>
            {currentSection.description && <p className="text-[var(--textMuted)] text-sm">{currentSection.description}</p>}
          </div>
          <div className="space-y-8">
            {currentSection.fieldIds.map(renderField)}
          </div>
          <div className="flex items-center justify-between mt-12 pt-8 border-t border-[var(--border)]">
            <button type="button" disabled={currentSectionIndex === 0} onClick={() => setCurrentSectionIndex(prev => prev - 1)} className="flex items-center gap-2 px-6 py-3 font-bold text-[var(--textMuted)] hover:text-[var(--text)] disabled:opacity-30 disabled:cursor-not-allowed transition-all" >
              <SafeIcon name="ArrowLeft" /> Back
            </button>
            {currentSectionIndex < form.sections.length - 1 ? (
              <button type="button" disabled={!isSectionValid()} onClick={() => setCurrentSectionIndex(prev => prev + 1)} className="flex items-center gap-2 px-8 py-3 bg-[var(--primary)] text-white font-bold rounded-xl hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-500/20" >
                Next Step <SafeIcon name="ArrowRight" />
              </button>
            ) : (
              <button type="button" disabled={!isSectionValid() || isSubmitting} onClick={handleFinalSubmit} className="flex items-center gap-3 px-10 py-4 bg-[var(--success)] text-white font-black rounded-xl hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-xl shadow-green-500/20" >
                {isSubmitting ? (
                  <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1 }}>
                    <SafeIcon name="Loader" />
                  </motion.div>
                ) : (
                  <> Submit Intake <SafeIcon name="Send" /> </>
                )}
              </button>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default FormRenderer;